import React from "react";
import {
  Checkbox,
  Chip,
  IconButton,
  InputAdornment,
  TextField,
} from "@mui/material";
import { filesize } from "filesize";
import { gql } from "@apollo/client";

import UserAvatars from "blocks/UserAvatars";
import ItemPath, { ItemPathFragment } from "blocks/ItemPath";
import useData from "hooks/useData";

import DateTime from "./DateTime";
import { showMessage } from "helpers";
import { ClipboardOutline } from "mdi-material-ui";

export const TEXT_PADDING = 10;

export function TextCell({ value }) {
  return (
    <span style={{ display: "inline-block", margin: TEXT_PADDING }}>
      {value}
    </span>
  );
}

export function LinkCell({
  value: { label, title = label, href, onClick, disabled },
}) {
  return (
    <a
      style={{
        color: "inherit",
        display: "inline-block",
        margin: TEXT_PADDING,
      }}
      href={href || "#"}
      onClick={onClick}
      disabled={disabled}
    >
      {title}
    </a>
  );
}

export function ComponentCell({ value: { component } }) {
  return <>{component}</>;
}

export function UrlCell({ value: url }) {
  return (
    <TextField
      variant="outlined"
      size="small"
      value={url}
      style={{ width: 300, margin: 0, padding: 5 }}
      error={!url}
      helperText={!url && <>(链接不可用)</>}
      slotProps={{
        input: {
          ...(url && {
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  title="复制到剪切板"
                  size="small"
                  color="primary"
                  edge="end"
                  disabled={!navigator.clipboard?.writeText}
                  onClick={async () => {
                    await navigator.clipboard.writeText(url);
                    showMessage({ message: "成功复制链接到剪切板" });
                  }}
                >
                  <ClipboardOutline />
                </IconButton>
              </InputAdornment>
            ),
          }),
        },
      }}
    ></TextField>
  );
}

export function FileSizeCell({ value }) {
  return (
    <span style={{ display: "inline-block", margin: TEXT_PADDING }}>
      {filesize(value)}
    </span>
  );
}

export function ItemPathCell({ value: itemId }) {
  const data = useData(
    gql`
      query ItemPathCell($itemId: ID!) {
        item(id: $itemId) {
          id
          ...ItemPathFragment
        }
      }
      ${ItemPathFragment}
    `,
    { itemId },
  );
  return (
    <ItemPath clickable item={data?.item} style={{ margin: TEXT_PADDING }} />
  );
}

export function DateTimeCell({ value }) {
  return (
    <span style={{ display: "inline-block", margin: TEXT_PADDING }}>
      <DateTime value={value} />
    </span>
  );
}

export function CheckboxCell({
  value: { checked, onChange, disabled, title },
}) {
  return (
    <Checkbox
      component="button"
      checked={checked}
      readOnly={!onChange}
      disabled={disabled}
      onChange={(event) => {
        if (onChange) onChange(event.target.checked);
      }}
      title={title}
    />
  );
}

export function IconButtonCell({
  value: { onClick, href, target, title, icon },
}) {
  return (
    <IconButton
      href={href || "#"}
      onClick={onClick}
      target={target}
      title={title}
      size="large"
    >
      {icon}
    </IconButton>
  );
}

export function UsersCell({ value }) {
  return (
    <UserAvatars
      userAvatarProps={{
        style: {
          width: 32,
          height: 32,
        },
        triggerUserDialog: true,
      }}
      style={{
        display: "inline-flex",
      }}
      userIds={value}
    />
  );
}

export function ChipsCell({ value: chips }) {
  return (
    <div
      style={{
        display: "flex",
        flexFlow: "row wrap",
      }}
    >
      {chips?.map((chip) => (
        <Chip
          size="small"
          clickable
          component="a"
          key={chip.label}
          label={chip.label}
          icon={chip.icon}
          href={chip.href || "#"}
          style={{ margin: 3 }}
        />
      ))}
    </div>
  );
}
